import React from 'react';
import {
  formatMoney,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import startCase from 'lodash/startCase';
import {
  InsuranceBalanceExplanationAmountLineDTO,
} from 'dtos/insuranceBalanceExplanationAmountLineDTO';
import {
  InsuranceBalanceExplanationDTO,
} from 'dtos/insuranceBalanceExplanationDTO';
import {
  InsuranceBalanceExplanationHeaderAmountDTO,
} from 'dtos/insuranceBalanceExplanationHeaderAmountDTO';

type MetricProps = {
  label: string;
  value: string;
};

type SectionProps = {
  label: string;
  text: string;
};

function moneyOrDash(amount?: number | null) {
  return isNil(amount) ? '--' : formatMoney(amount);
}

function Metric({
  label,
  value,
}: MetricProps) {
  return (
    <div className="flex flex-col gap-0.5 px-3 first:pl-0 last:pr-0">
      <span className="text-gray-500 text-xs">{label}</span>
      <span className="font-semibold text-sm">{value}</span>
    </div>
  );
}

function SectionRow({
  label,
  text,
}: SectionProps) {
  return (
    <div className="flex flex-col gap-0.5 py-2">
      <div className="flex items-center gap-1.5 font-semibold text-xs">
        {label}
      </div>
      <span className="text-gray-500 text-xs">{text}</span>
    </div>
  );
}

function AmountLine({
  label,
  explanation,
  amount,
}: InsuranceBalanceExplanationAmountLineDTO) {
  return (
    <div className="flex items-start justify-between p-2 pl-0 gap-2 text-xs py-0.5">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-gray-500" />
          <span className="text-gray-700 font-medium">{label ?? '--'}</span>
        </div>
        <span className="text-gray-500 flex-1 pl-3">{explanation ?? '--'}</span>
      </div>
      <span className="font-semibold text-gray-700 text-right">
        {isNil(amount) ? '--' : formatMoney(amount)}
      </span>
    </div>
  );
}

type AmountSectionProps = {
  title: string;
  lines: InsuranceBalanceExplanationAmountLineDTO[];
};

function AmountSection({
  title,
  lines,
}: AmountSectionProps) {
  return lines.length > 0 ? (
    <div className="flex flex-col gap-1 pt-1">
      <div className="flex items-center gap-2">
        <span className="text-gray-900 w-32 shrink-0 font-semibold text-xs">{title}</span>
      </div>
      {lines.map((line) => (
        <AmountLine
          key={`${title}-${line.code ?? 'row'}-${line.label ?? ''}-${String(line.amount)}-${line.explanation ?? ''}`}
          {...line}
        />
      ))}
    </div>
  ) : null;
}

type Props = {
  data: InsuranceBalanceExplanationDTO;
};

export default function InsuranceBalanceExplanationContent({
  data,
}: Props) {
  const title = data.headline ?? (!isNil(data.scenarioCode) ? startCase(data.scenarioCode) : null);
  const hasSummarySection = !isNil(title) && !isNil(data.summary);
  const determiningFactorsTitle = data.determiningFactorsHeading ?? 'Determining Factors';
  const hasDeterminingFactors = (data.determiningFactors?.length ?? 0) > 0;

  const headerMetrics: InsuranceBalanceExplanationHeaderAmountDTO[] = (
    (data.headerAmounts?.length ?? 0) > 0
      ? (data.headerAmounts ?? [])
      : [
        {
          code: 'CurrentBalance',
          label: 'Current Balance',
          amount: data.currentInsuranceBalance,
        },
        {
          code: 'ResolvedAmount',
          label: data.isResolved
            ? 'Resolved Amount'
            : `Resolved Amount (${data.hasAppliedResolution ? 'Applied' : 'Pending'})`,
          amount: data.resolvedAmount,
        },
      ]
  );

  const hasAmountSection = (
    (data.includedAmounts?.length ?? 0) > 0
    || (data.resolutionAmounts?.length ?? 0) > 0
    || (data.excludedAmounts?.length ?? 0) > 0
    || hasDeterminingFactors
  );

  return (
    <div className="flex flex-col w-full gap-1 flex-1">
      <div className="flex items-center gap-2 divide-x divide-gray-200 mb-1">
        {headerMetrics.map((headerMetric) => (
          <Metric
            key={`${headerMetric.code ?? 'header-metric'}-${headerMetric.label ?? ''}-${String(headerMetric.amount)}`}
            label={headerMetric.label ?? '--'}
            value={moneyOrDash(headerMetric.amount)}
          />
        ))}
      </div>
      {hasSummarySection && (
        <>
          <div className="border-t border-gray-100" />
          <SectionRow
            label={title ?? ''}
            text={data.summary ?? ''}
          />
        </>
      )}
      {!isNil(data.nextStep?.text) && (
        <>
          <div className="border-t border-gray-100" />
          <SectionRow
            label="Next Step"
            text={data.nextStep?.text ?? ''}
          />
        </>
      )}
      {hasAmountSection && (
        <>
          <div className="border-t border-gray-100" />
          <div className="flex flex-col gap-2 pt-1 divide-y">
            <AmountSection
              title="Included Amounts"
              lines={data.includedAmounts ?? []}
            />
            <AmountSection
              title="Resolution Amounts"
              lines={data.resolutionAmounts ?? []}
            />
            <AmountSection
              title="Excluded Amounts"
              lines={data.excludedAmounts ?? []}
            />
            {hasDeterminingFactors && (
              <div className="flex flex-col gap-0.5 py-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-gray-900 w-full shrink-0 font-semibold">{determiningFactorsTitle}</span>
                </div>
                {data.determiningFactors?.map((factor) => (
                  <div
                    className="flex items-start gap-1.5 p-2 pl-0"
                    key={`determining-factor-${factor}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-gray-500 mt-[5px]" />
                    <span className="text-gray-700">{factor}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
